import React from 'react';
import { Badge, Button, Spinner, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCheckCircle, faRobot } from '@fortawesome/free-solid-svg-icons';

const MeetSyncSection = ({ 
  meetStatus, 
  loadingStatus, 
  syncing, 
  syncResult,
  error,
  onSync,
  onResync,
  eventDbId,
  onRefreshStatus
}) => {
  // El auto-sync se activa automáticamente al sincronizar
  const isAutoSyncActive = meetStatus?.auto_sync_active;
  const nextSync = meetStatus?.next_sync;

  if (loadingStatus) {
    return (
      <div className="text-center py-2">
        <Spinner animation="border" size="sm" className="me-2" />
        <small className="text-muted">Verificando estado de Google Meet...</small>
      </div>
    );
  }

  if (!meetStatus) return null;

  const getMeetBadge = () => {
    if (!meetStatus.has_meet) {
      return <Badge bg="secondary">Sin enlace de google Meet</Badge>;
    }

    if (meetStatus.synced && !meetStatus.needs_resync) {
      return <Badge bg="success">Realizada ✓</Badge>;
    }

    if (meetStatus.needs_resync) {
      // Usar pending_in_meet si está disponible, sino calcular la diferencia
      const pendingInMeet = meetStatus.pending_in_meet ?? 
        (meetStatus.participants_in_meet - meetStatus.participants_in_system);
      
      if (pendingInMeet > 0) {
        return <Badge bg="warning">{pendingInMeet} pendiente{pendingInMeet !== 1 ? 's' : ''} en google Meet</Badge>;
      } else {
        // Solo hay usuarios externos pendientes
        return <Badge bg="info">Todos sincronizados desde google Meet</Badge>;
      }
    }

    if (!meetStatus.synced && meetStatus.has_meet) {
      return <Badge bg="info">Pendiente de sincronizar</Badge>;
    }

    return null;
  };

  const handleResyncClick = () => {
    onResync();
  };

  return (
    <div className="mb-3 p-3 border rounded bg-light">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <strong>Sincronización con Google Meet:</strong> {getMeetBadge()}
        </div>
        {meetStatus?.has_meet && onRefreshStatus && (
          <Button
            variant="link"
            size="sm"
            onClick={onRefreshStatus}
            disabled={loadingStatus}
            className="p-0 text-muted"
            title="Actualizar estado"
          >
            <FontAwesomeIcon icon={faSync} spin={loadingStatus} />
          </Button>
        )}
      </div>

      {meetStatus.has_meet && (
        <>
          <div className="mb-2">
            <small className="text-muted">
              Internos: {(meetStatus.internos_invitados || 0) + (meetStatus.internos_no_invitados || 0)} (invitados: {meetStatus.internos_invitados || 0}, no invitados: {meetStatus.internos_no_invitados || 0})
              <br />
              Externos: {(meetStatus.externos_invitados || 0) + (meetStatus.externos_no_invitados || 0)} (invitados: {meetStatus.externos_invitados || 0}, no invitados: {meetStatus.externos_no_invitados || 0})
            </small>
          </div>

          {isAutoSyncActive && nextSync && (
            <Alert variant="info" className="mb-2 py-2">
              <small>
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                <strong>Auto sincronización activa</strong> - Próxima: {new Date(nextSync).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </small>
            </Alert>
          )}
        </>
      )}

      {syncResult && (
        <>
          <Alert variant="success" className="mb-2 py-2">
            <small>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              <strong>Sincronización exitosa:</strong> {(syncResult.attendances_created || 0) + (syncResult.attendances_updated || 0)} invitados registrados, {syncResult.attendances_skipped || 0} pendientes ({syncResult.total_participants || 0} usuarios en total)
            </small>
          </Alert>
          
          {syncResult.auto_sync_enabled && (
            <Alert variant="info" className="mb-2 py-2">
              <small>
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                <strong>Auto sincronización activa:</strong> Sincronización cada 30 min por 24h
                {syncResult.auto_sync_reason && (
                  <><br />{syncResult.auto_sync_reason}</>
                )}
              </small>
            </Alert>
          )}
          
          {!syncResult.auto_sync_enabled && syncResult.auto_sync_reason && (
            <Alert variant="secondary" className="mb-2 py-2">
              <small>
                <strong>Sincronización automática:</strong> {syncResult.auto_sync_reason === 'No hay usuarios internos pendientes de sincronizar' ? 'Todos los participantes internos ya están registrados' : syncResult.auto_sync_reason}
              </small>
            </Alert>
          )}
          
          {syncResult.attendances_skipped > 0 && (
            <Alert variant="warning" className="mb-2 py-2">
              <small>
                <strong>⚠️ {syncResult.attendances_skipped} usuario{syncResult.attendances_skipped !== 1 ? 's' : ''} debe{syncResult.attendances_skipped !== 1 ? 'n' : ''} usar QR/Link</strong>
                {syncResult.attendances_skipped_meet > 0 && (
                  <><br />• {syncResult.attendances_skipped_meet} en google Meet (pendiente{syncResult.attendances_skipped_meet !== 1 ? 's' : ''} de sincronizar)</>
                )}
                {syncResult.attendances_skipped_external > 0 && (
                  <><br />• {syncResult.attendances_skipped_external} externo{syncResult.attendances_skipped_external !== 1 ? 's' : ''} (debe{syncResult.attendances_skipped_external !== 1 ? 'n' : ''} usar QR/Link)</>
                )}
              </small>
            </Alert>
          )}
        </>
      )}

      {error && (
        <Alert variant="danger" className="mb-2 py-2">
          <small>{error}</small>
        </Alert>
      )}

      {meetStatus.can_sync && (
        <div className="d-flex gap-2 flex-wrap">
          {!meetStatus.synced && (
            <Button
              variant="primary"
              size="sm"
              onClick={onSync}
              disabled={syncing}
              title="Sincroniza la asistencia desde Google Meet y mantiene la sincronización activa por 24 horas"
            >
              {syncing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSync} className="me-1" />
                  Sincronizar Asistencia
                </>
              )}
            </Button>
          )}

          {meetStatus.synced && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleResyncClick}
              disabled={syncing}
              title="Actualiza la asistencia desde Google Meet y mantiene la sincronización activa por 24 horas"
            >
              {syncing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Actualizando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSync} className="me-1" />
                  Actualizar Asistencia
                </>
              )}
            </Button>
          )}


        </div>
      )}

      {!meetStatus.has_meet && (
        <small className="text-muted">
          Este evento no tiene una reunión de Google Meet asociada
        </small>
      )}


    </div>
  );
};

export default MeetSyncSection;
